import { ThemeEvents } from '@theme/events';

const KLAVIYO_EVENT_NAME = 'Added to Cart';

/**
 * @returns {boolean}
 */
function hasMarketingConsent() {
  const privacy = window.Shopify?.customerPrivacy;

  if (!privacy?.currentVisitorConsent) {
    return true;
  }

  return Boolean(privacy.currentVisitorConsent().marketing);
}

/**
 * @returns {Array}
 */
function getLearnq() {
  if (!Array.isArray(window._learnq)) {
    window._learnq = [];
  }

  return window._learnq;
}

/**
 * @param {number | undefined} cents
 * @returns {number | undefined}
 */
function centsToCurrency(cents) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return undefined;

  return Number((cents / 100).toFixed(2));
}

/**
 * @param {string | undefined} path
 * @returns {string | undefined}
 */
function toAbsoluteUrl(path) {
  if (!path) return undefined;

  if (/^https?:\/\//i.test(path)) return path;

  return `${window.location.origin}${path}`;
}

/**
 * @param {Object} object
 * @returns {Object}
 */
function compactObject(object) {
  return Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined && value !== null));
}

/**
 * @param {string} variantId
 * @returns {Promise<any | undefined>}
 */
async function getCartLineByVariantId(variantId) {
  if (!variantId) return undefined;

  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });

    if (!response.ok) return undefined;

    const cart = await response.json();
    if (!Array.isArray(cart?.items)) return undefined;

    return cart.items.find((item) => String(item.variant_id) === String(variantId));
  } catch (_) {
    return undefined;
  }
}

/**
 * @param {Event} event
 */
async function handleCartUpdate(event) {
  const detail = event?.detail ?? {};
  const data = detail?.data ?? {};

  if (data.source !== 'product-form-component' || data.didError) {
    return;
  }

  if (!hasMarketingConsent()) {
    return;
  }

  const sourceElement = event.target instanceof HTMLElement ? event.target : null;
  const productForm = sourceElement?.closest('product-form-component') ?? sourceElement;

  const variantId =
    data.variantId ||
    detail.sourceId ||
    productForm?.querySelector?.('input[name="id"]')?.value;

  const quantity = Number(data.itemCount) || 1;
  const cartItem = await getCartLineByVariantId(String(variantId || ''));

  const payload = compactObject({
    ProductID: cartItem?.product_id || data.productId || productForm?.dataset?.productId,
    VariantID: cartItem?.variant_id || variantId,
    Name: cartItem?.product_title,
    VariantName: cartItem?.variant_title,
    Brand: cartItem?.vendor,
    SKU: cartItem?.sku,
    Quantity: quantity,
    Price: centsToCurrency(cartItem?.price),
    CompareAtPrice: centsToCurrency(cartItem?.original_price),
    ImageURL: cartItem?.featured_image?.url,
    URL: toAbsoluteUrl(cartItem?.url || productForm?.dataset?.productUrl),
    $value: centsToCurrency((cartItem?.final_price || cartItem?.price || 0) * quantity),
  });

  getLearnq().push(['track', KLAVIYO_EVENT_NAME, payload]);
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
